/**
 * Systemvariablen-Katalog für Vertragsvorlagen. Jede Variable hat einen Pfad
 * `namespace.key`, einen Typ (bestimmt die Standardformatierung beim Rendern),
 * Bezeichnungen in de/en und ein erfundenes Beispiel für die Vorlagenhilfe.
 *
 * Der Katalog ist die einzige Quelle dafür, welche Platzhalter eine Vorlage
 * benutzen darf. Eigene Variablen einer Agentur laufen als `var.<key>` und
 * werden über CustomVariableDef beschrieben.
 */
import type { Cents, IsoDate, MonthKey } from "./types.js";

export type ContractRole = "agency" | "brand" | "talent";
export const CONTRACT_ROLES: readonly ContractRole[] = ["agency", "brand", "talent"];

export function isContractRole(value: unknown): value is ContractRole {
  return typeof value === "string" && (CONTRACT_ROLES as readonly string[]).includes(value);
}

export type VariableType = "text" | "money" | "date" | "percent" | "number" | "boolean" | "lines";

export interface SystemVariable {
  /** `namespace.key`, z. B. 'agency.legal_name' */
  path: string;
  type: VariableType;
  label: { de: string; en: string };
  /** erfundenes Beispiel für die Vorlagenhilfe (Geld als Cent-Zahl) */
  example: string | number | boolean;
}

/** Beschreibung einer eigenen Variablen der Agentur (`{{var.key}}`). */
export interface CustomVariableDef {
  key: string;
  label: { de: string; en: string };
  type: VariableType;
  required: boolean;
  default?: unknown;
  /** Wer den Wert beim Unterschreiben einträgt, wenn er beim Rendern fehlt */
  fillable_by?: ContractRole;
  /** Auswahlliste (Text-Variablen) */
  options?: string[];
  /**
   * Systempfad, aus dem die App den Wert beim Anlegen vorbelegt (z. B. 'talent.commission_percent'), solange er leer ist.
   * Der Renderer liest ihn nicht: vorbelegt wird einmal, danach gilt der eingetragene Wert. Weicht er später von den
   * Stammdaten ab, meldet die Prüfung einen Hinweis (MASTER_DATA_MISMATCH).
   */
  prefill?: string;
}

const V = (path: string, type: VariableType, de: string, en: string, example: string | number | boolean): SystemVariable => ({
  path,
  type,
  label: { de, en },
  example,
});

export const SYSTEM_VARIABLES: readonly SystemVariable[] = [
  // Agentur
  V("agency.legal_name", "text", "Agentur: Firmenname", "Agency: legal name", "Beispiel Management Ltd."),
  V("agency.trade_name", "text", "Agentur: Markenname", "Agency: trade name", "Beispiel Management"),
  V("agency.address_block", "lines", "Agentur: Anschrift (mehrzeilig)", "Agency: address block", "Musterweg 1\n12345 Musterstadt\nDeutschland"),
  V("agency.street", "text", "Agentur: Straße", "Agency: street", "Musterweg 1"),
  V("agency.postal_code", "text", "Agentur: PLZ", "Agency: postal code", "12345"),
  V("agency.city", "text", "Agentur: Ort", "Agency: city", "Musterstadt"),
  V("agency.country_name", "text", "Agentur: Land", "Agency: country", "Deutschland"),
  V("agency.vat_id", "text", "Agentur: USt-IdNr.", "Agency: VAT ID", "DE000000000"),
  V("agency.tax_number", "text", "Agentur: Steuernummer", "Agency: tax number", "00/000/00000"),
  V("agency.register_info", "text", "Agentur: Registerangaben", "Agency: register info", "Register-Nr. 000000"),
  V("agency.representative_name", "text", "Agentur: Vertretungsberechtigte Person", "Agency: representative", "Erika Beispiel"),
  V("agency.representative_position", "text", "Agentur: Position der Vertretung", "Agency: representative position", "Geschäftsführung"),
  // 0071: die im Vertrag gewählten Vertretungen mit Funktion, beim Vier-Augen-Prinzip beide (Gesamtvertretung)
  V("agency.represented_by", "text", "Agentur: vertreten durch (Name und Funktion)", "Agency: represented by (name and position)", "Erika Beispiel, Geschäftsführerin"),
  V("agency.email", "text", "Agentur: E-Mail", "Agency: email", "kontakt@example.com"),
  V("agency.phone", "text", "Agentur: Telefon", "Agency: phone", "+49 30 0000000"),
  V("agency.website", "text", "Agentur: Website", "Agency: website", "https://example.com"),
  V("agency.governing_law", "text", "Agentur: anwendbares Recht", "Agency: governing law", "Recht der Bundesrepublik Deutschland"),
  V("agency.jurisdiction", "text", "Agentur: Gerichtsstand", "Agency: jurisdiction", "Musterstadt"),
  V("agency.default_commission_percent", "percent", "Agentur: Standard-Provision", "Agency: default commission", 25),
  V("agency.payout_days", "number", "Agentur: Auszahlung an Creator in Tagen", "Agency: payout to creators in days", 14),
  V("agency.payment_terms_days", "number", "Agentur: Zahlungsziel der Marken in Tagen", "Agency: brand payment terms in days", 14),
  V("agency.invoice_route", "text", "Agentur: Abrechnungsweg", "Agency: billing route", "Die Agentur stellt der Marke die Rechnung."),
  V("agency.invoices_brand", "boolean", "Agentur: stellt der Marke die Rechnung", "Agency: invoices the brand", true),
  // SAAS-6: Auszahlung an Creator erst nach Zahlungseingang der Marke (sonst feste Frist, Plattform-Standard)
  // SAAS-12: Steuerwort im Sitzland der Agentur („Umsatzsteuer“, in der Schweiz „Mehrwertsteuer“; englisch „VAT“)
  V("agency.vat_term", "text", "Agentur: Steuerwort (Umsatzsteuer bzw. Mehrwertsteuer)", "Agency: VAT term", "Umsatzsteuer"),
  V("agency.payout_after_brand_payment", "boolean", "Agentur: zahlt Creator erst nach Zahlung der Marke aus", "Agency: pays creators only after the brand has paid", false),
  // Rechtsraum aus der Rechtswahl der Agentur (sonst ihr Sitz): für Klauseln, die je Land anders lauten (Befund 23)
  V("agency.law_de", "boolean", "Agentur: deutsches Recht", "Agency: German law", true),
  V("agency.law_at", "boolean", "Agentur: österreichisches Recht", "Agency: Austrian law", false),
  V("agency.law_ch", "boolean", "Agentur: Schweizer Recht", "Agency: Swiss law", false),
  // K-403d (0302): Sitzland der Agentur, unabhängig von der Rechtswahl (z. B. Künstlersozialabgabe des deutschen Verwerters)
  V("agency.seat_de", "boolean", "Agentur: Sitz in Deutschland", "Agency: seat in Germany", true),
  // Creator
  V("talent.full_name", "text", "Creator: vollständiger Name", "Creator: full name", "Mira Beispiel"),
  V("talent.first_name", "text", "Creator: Vorname", "Creator: first name", "Mira"),
  V("talent.last_name", "text", "Creator: Nachname", "Creator: last name", "Beispiel"),
  V("talent.address_block", "lines", "Creator: Anschrift (mehrzeilig)", "Creator: address block", "Beispielgasse 2\n54321 Beispielstadt\nÖsterreich"),
  V("talent.street", "text", "Creator: Straße", "Creator: street", "Beispielgasse 2"),
  V("talent.postal_code", "text", "Creator: PLZ", "Creator: postal code", "54321"),
  V("talent.city", "text", "Creator: Ort", "Creator: city", "Beispielstadt"),
  V("talent.country_name", "text", "Creator: Land", "Creator: country", "Österreich"),
  V("talent.email", "text", "Creator: E-Mail", "Creator: email", "mira@example.com"),
  V("talent.vat_id", "text", "Creator: USt-IdNr.", "Creator: VAT ID", "ATU00000000"),
  V("talent.tax_number", "text", "Creator: Steuernummer", "Creator: tax number", "00 000/0000"),
  V("talent.small_business", "boolean", "Creator: Kleinunternehmer", "Creator: small business", false),
  V("talent.instagram_handle", "text", "Creator: Instagram-Handle", "Creator: Instagram handle", "@mira.beispiel"),
  V("talent.commission_percent", "percent", "Creator: Provisionssatz der Agentur", "Creator: agency commission rate", 25),
  V("talent.company_name", "text", "Creator: Firma (falls über eigene Firma)", "Creator: company (if contracting through a company)", "Mira Media GmbH"),
  V("talent.birth_date", "date", "Creator: Geburtsdatum", "Creator: date of birth", "2009-05-01"),
  V("talent.is_minor", "boolean", "Creator: bei der Unterschrift minderjährig", "Creator: minor when signing", false),
  V("talent.guardian_names", "text", "Creator: gesetzliche Vertretung (Namen)", "Creator: legal guardian(s)", "Petra Beispiel und Tom Beispiel"),
  // Marke
  V("brand.legal_name", "text", "Marke: Firmenname", "Brand: legal name", "Beispielmarke GmbH"),
  V("brand.name", "text", "Marke: Name", "Brand: name", "Beispielmarke"),
  V("brand.address_block", "lines", "Marke: Anschrift (mehrzeilig)", "Brand: address block", "Industriestraße 3\n67890 Beispielhausen\nDeutschland"),
  V("brand.street", "text", "Marke: Straße", "Brand: street", "Industriestraße 3"),
  V("brand.postal_code", "text", "Marke: PLZ", "Brand: postal code", "67890"),
  V("brand.city", "text", "Marke: Ort", "Brand: city", "Beispielhausen"),
  V("brand.country_name", "text", "Marke: Land", "Brand: country", "Deutschland"),
  V("brand.vat_id", "text", "Marke: USt-IdNr.", "Brand: VAT ID", "DE111111111"),
  V("brand.register_info", "text", "Marke: Registerangaben", "Brand: register info", "Register-Nr. 111111"),
  V("brand.represented_by", "text", "Marke: vertreten durch (Agentur der Marke)", "Brand: represented by (brand's agency)", "Beispiel Media GmbH"),
  V("brand.contact_name", "text", "Marke: Ansprechperson", "Brand: contact name", "Jonas Beispiel"),
  V("brand.contact_email", "text", "Marke: E-Mail der Ansprechperson", "Brand: contact email", "jonas@example.com"),
  // Kooperation
  V("deal.title", "text", "Kooperation: Titel", "Collaboration: title", "Frühjahrskampagne"),
  V("deal.budget", "money", "Kooperation: Budget", "Collaboration: budget", 250_000),
  V("deal.currency", "text", "Kooperation: Währung", "Collaboration: currency", "EUR"),
  V("deal.commission_percent", "percent", "Kooperation: Provisionssatz", "Collaboration: commission rate", 25),
  V("deal.talent_share", "money", "Kooperation: Anteil Creator", "Collaboration: creator share", 187_500),
  V("deal.agency_fee", "money", "Kooperation: Agenturgebühr", "Collaboration: agency fee", 0),
  V("deal.model", "text", "Kooperation: Vergütungsmodell", "Collaboration: remuneration model", "Festpreis"),
  V("deal.is_barter", "boolean", "Kooperation: Produkte statt Geld (Barter, Gifting)", "Collaboration: products instead of money", false),
  V("deal.is_commission_only", "boolean", "Kooperation: nur Provision", "Collaboration: commission only", false),
  V("deal.is_recurring", "boolean", "Kooperation: monatlich oder in Raten", "Collaboration: monthly or in instalments", false),
  V("deal.items", "lines", "Kooperation: alle Positionen", "Collaboration: all items", "Nutzungsrechte Paid Social 6 Monate: 500,00 €"),
  V("deal.extras", "lines", "Kooperation: Zusatzpositionen", "Collaboration: additional items", "Whitelisting 3 Monate: 300,00 €"),
  V("deal.consideration", "lines", "Kooperation: Gegenleistung ohne Geld", "Collaboration: non-cash consideration", "Produktpaket Frühjahr"),
  V("deal.items_total", "money", "Kooperation: Summe der Zusatzpositionen", "Collaboration: total of additional items", 50_000),
  V("deal.total", "money", "Kooperation: Gesamtbetrag", "Collaboration: total amount", 300_000),
  V("deal.monthly_schedule", "lines", "Kooperation: Beträge je Monat", "Collaboration: amounts per month", "Oktober 2026: 1.000,00 €\nNovember 2026: 1.000,00 €"),
  V("deal.start_month", "text", "Kooperation: Startmonat", "Collaboration: start month", "2026-10"),
  V("deal.end_month", "text", "Kooperation: Endmonat", "Collaboration: end month", "2026-12"),
  V("deal.months", "number", "Kooperation: Laufzeit in Monaten", "Collaboration: number of months", 3),
  V("deal.deliverables", "lines", "Kooperation: Leistungen", "Collaboration: deliverables", "2 Reels\n4 Stories"),
  // 0068: Beiträge der Kooperation als Liste mit Fristen; ohne angelegte Beiträge der Freitext der Leistungen
  V("deal.deliverables_list", "lines", "Kooperation: Beiträge mit Fristen", "Collaboration: content pieces with dates",
    "2 × Reel · Instagram: Entwurf bis 01.10.2026, Veröffentlichung am 15.10.2026, Nutzungsrechte 6 Monate ab Veröffentlichung\n4 × Story · Instagram: Veröffentlichung am 16.10.2026"),
  V("deal.platforms", "text", "Kooperation: Plattformen", "Collaboration: platforms", "Instagram, TikTok"),
  V("deal.usage_rights", "lines", "Kooperation: Nutzungsrechte", "Collaboration: usage rights", "Organisch 12 Monate\nPaid 3 Monate"),
  // VERTRAEGE-R3-07: Nutzungsrechte aus den Beiträgen (eine Wahrheit mit § 2), ohne Beiträge mit Dauer der Freitext
  V("deal.usage_rights_list", "lines", "Kooperation: Nutzungsrechte je Beitrag", "Collaboration: usage rights per content piece",
    "2 × Reel · Instagram: 6 Monate ab Veröffentlichung\n4 × Story · Instagram: 1 Monat ab Veröffentlichung"),
  V("deal.usage_rights_by_piece", "boolean", "Kooperation: Nutzungsrechte stehen an jedem Beitrag", "Collaboration: usage rights set on every content piece", true),
  V("deal.usage_rights_months", "number", "Kooperation: Nutzungsrechte in Monaten (gleich für alle Beiträge)", "Collaboration: usage rights in months (same for all pieces)", 6),
  V("deal.usage_rights_until", "date", "Kooperation: Nutzungsrechte bis", "Collaboration: usage rights until", "2027-06-30"),
  V("deal.content_deadline", "date", "Kooperation: Frist für Inhalte", "Collaboration: content deadline", "2026-10-31"),
  V("deal.exclusive", "boolean", "Kooperation: Exklusivität", "Collaboration: exclusive", false),
  V("deal.exclusive_until", "date", "Kooperation: Exklusivität bis", "Collaboration: exclusivity until", "2027-03-31"),
  V("deal.exclusive_scope", "text", "Kooperation: Exklusivität für Branche oder Wettbewerber", "Collaboration: exclusivity scope (industry or competitors)", "Sonnenpflege, keine anderen Kosmetikmarken"),
  // Wer der Marke die Rechnung stellt und an wen sie zahlt (Abrechnungsweg der Agentur, Befund 22)
  V("deal.invoice_issuer", "text", "Kooperation: stellt der Marke die Rechnung", "Collaboration: invoices the brand", "Beispiel Management Ltd."),
  V("deal.payee", "text", "Kooperation: Zahlungsempfänger der Marke", "Collaboration: payee of the brand", "Beispiel Management Ltd."),
  // Vertrag
  V("contract.number", "text", "Vertrag: Nummer", "Contract: number", "V-2026-0001"),
  V("contract.title", "text", "Vertrag: Titel", "Contract: title", "Kooperationsvertrag"),
  V("contract.date", "date", "Vertrag: Datum", "Contract: date", "2026-09-24"),
  V("contract.kind", "text", "Vertrag: Art", "Contract: kind", "deal"),
  V("contract.language", "text", "Vertrag: Sprache", "Contract: language", "de"),
  V("contract.starts_on", "date", "Vertrag: Beginn", "Contract: start", "2026-10-01"),
  V("contract.ends_on", "date", "Vertrag: Ende", "Contract: end", "2027-09-30"),
  // Laufzeit und Fristen als Felder des Vertrags (Befund 10): dieselben Werte speisen Erinnerung und Verlängerung
  V("contract.term_months", "number", "Vertrag: Laufzeit in Monaten", "Contract: term in months", 12),
  V("contract.auto_renew", "boolean", "Vertrag: verlängert sich automatisch", "Contract: renews automatically", true),
  V("contract.renewal_months", "number", "Vertrag: Verlängerung in Monaten", "Contract: renewal in months", 12),
  V("contract.notice_days", "number", "Vertrag: Kündigungsfrist in Tagen", "Contract: notice period in days", 28),
  V("contract.notice_period", "text", "Vertrag: Kündigungsfrist (Text)", "Contract: notice period (text)", "4 Wochen"),
  V("contract.four_eyes", "boolean", "Vertrag: zwei Unterschriften der Agentur", "Contract: two agency signatures", false),
  V("contract.exclusive", "boolean", "Vertrag: exklusive Vertretung", "Contract: exclusive representation", false),
  V("contract.exclusive_scope", "text", "Vertrag: Bereich der Exklusivität", "Contract: scope of exclusivity", "Beauty und Pflege"),
  V("contract.tail_months", "number", "Vertrag: Nachlauf in Monaten", "Contract: tail period in months", 6),
  // Ursprungsvertrag eines Nachtrags (Praxis)
  V("original.number", "text", "Ursprungsvertrag: Nummer", "Original agreement: number", "V-0012"),
  V("original.title", "text", "Ursprungsvertrag: Titel", "Original agreement: title", "Kooperationsvertrag Frühjahr"),
  V("original.date", "date", "Ursprungsvertrag: unterschrieben am", "Original agreement: signed on", "2026-03-01"),
  V("original.reference", "text", "Ursprungsvertrag: Bezeichnung mit Datum", "Original agreement: reference with date", "V-0012 vom 01.03.2026"),
  // Datum
  V("dates.today", "date", "Heute", "Today", "2026-09-24"),
  V("dates.year", "number", "Jahr", "Year", 2026),
];

export const SYSTEM_VARIABLE_BY_PATH: ReadonlyMap<string, SystemVariable> = new Map(SYSTEM_VARIABLES.map((v) => [v.path, v]));

export function systemVariable(path: string): SystemVariable | undefined {
  return SYSTEM_VARIABLE_BY_PATH.get(path);
}

export function isSystemVariablePath(path: string): boolean {
  return SYSTEM_VARIABLE_BY_PATH.has(path);
}

// ---------------------------------------------------------------------------
// Kontext (Werte je Namensraum); Geld in Cent, Datum als ISO-String
// ---------------------------------------------------------------------------

type Text = string | null;

export interface AgencyContext {
  legal_name?: Text;
  trade_name?: Text;
  address_block?: Text;
  street?: Text;
  postal_code?: Text;
  city?: Text;
  country_name?: Text;
  vat_id?: Text;
  tax_number?: Text;
  register_info?: Text;
  representative_name?: Text;
  representative_position?: Text;
  represented_by?: Text;
  email?: Text;
  phone?: Text;
  website?: Text;
  governing_law?: Text;
  jurisdiction?: Text;
  default_commission_percent?: number | null;
  payout_days?: number | null;
  payment_terms_days?: number | null;
  invoice_route?: Text;
  invoices_brand?: boolean | null;
  payout_after_brand_payment?: boolean | null;
  vat_term?: Text;
  law_de?: boolean | null;
  law_at?: boolean | null;
  law_ch?: boolean | null;
  seat_de?: boolean | null;
}

export interface TalentContext {
  full_name?: Text;
  first_name?: Text;
  last_name?: Text;
  address_block?: Text;
  street?: Text;
  postal_code?: Text;
  city?: Text;
  country_name?: Text;
  email?: Text;
  vat_id?: Text;
  tax_number?: Text;
  small_business?: boolean | null;
  instagram_handle?: Text;
  commission_percent?: number | null;
  company_name?: Text;
  birth_date?: IsoDate | null;
  is_minor?: boolean | null;
  guardian_names?: Text;
}

export interface BrandContext {
  legal_name?: Text;
  name?: Text;
  address_block?: Text;
  street?: Text;
  postal_code?: Text;
  city?: Text;
  country_name?: Text;
  vat_id?: Text;
  register_info?: Text;
  represented_by?: Text;
  contact_name?: Text;
  contact_email?: Text;
}

export interface DealContext {
  title?: Text;
  budget?: Cents | null;
  currency?: Text;
  commission_percent?: number | null;
  talent_share?: Cents | null;
  agency_fee?: Cents | null;
  model?: Text;
  is_barter?: boolean | null;
  is_commission_only?: boolean | null;
  is_recurring?: boolean | null;
  items?: Text;
  extras?: Text;
  consideration?: Text;
  items_total?: Cents | null;
  total?: Cents | null;
  monthly_schedule?: Text;
  start_month?: MonthKey | null;
  end_month?: MonthKey | null;
  months?: number | null;
  deliverables?: Text;
  /** Beiträge als Liste mit Fristen (deliverablesListText), sonst der Freitext der Leistungen (0068) */
  deliverables_list?: Text;
  platforms?: Text;
  usage_rights?: Text;
  /** Nutzungsrechte je Beitrag (usageRightsFromPieces), sonst der Freitext (VERTRAEGE-R3-07) */
  usage_rights_list?: Text;
  /** jeder (nicht entfallene) Beitrag hat eine Dauer der Nutzungsrechte */
  usage_rights_by_piece?: boolean | null;
  /** gemeinsame Dauer aller Beiträge in Monaten; null, wenn sie abweichen oder fehlen */
  usage_rights_months?: number | null;
  usage_rights_until?: IsoDate | null;
  content_deadline?: IsoDate | null;
  /** CO-009: nur zur Vorbelegung der Angaben free_revisions und revision_fee (kein Platzhalter, der Server rendert sie nicht) */
  free_revisions?: number | null;
  revision_fee?: Cents | null;
  exclusive?: boolean | null;
  exclusive_until?: IsoDate | null;
  exclusive_scope?: Text;
  invoice_issuer?: Text;
  payee?: Text;
}

export interface ContractMetaContext {
  number?: Text;
  title?: Text;
  date?: IsoDate | null;
  kind?: Text;
  language?: Text;
  starts_on?: IsoDate | null;
  ends_on?: IsoDate | null;
  term_months?: number | null;
  auto_renew?: boolean | null;
  renewal_months?: number | null;
  notice_days?: number | null;
  notice_period?: Text;
  four_eyes?: boolean | null;
  exclusive?: boolean | null;
  exclusive_scope?: Text;
  tail_months?: number | null;
}

/** Ursprungsvertrag eines Nachtrags; ohne Nachtrag sind alle Werte leer (nicht „fehlend“). */
export interface OriginalContext {
  number?: Text;
  title?: Text;
  date?: IsoDate | null;
  reference?: Text;
}

export interface DatesContext {
  today?: IsoDate | null;
  year?: number | null;
}

export interface ContractContext {
  agency?: AgencyContext;
  talent?: TalentContext;
  brand?: BrandContext;
  deal?: DealContext;
  contract?: ContractMetaContext;
  original?: OriginalContext;
  dates?: DatesContext;
}

export type ContractNamespace = keyof ContractContext;
export const CONTRACT_NAMESPACES: readonly ContractNamespace[] = ["agency", "talent", "brand", "deal", "contract", "original", "dates"];

/** Rohwert einer Systemvariablen aus dem Kontext (undefined = nicht geliefert). */
export function contextValue(context: ContractContext, path: string): unknown {
  const dot = path.indexOf(".");
  if (dot < 0) return undefined;
  const ns = path.slice(0, dot) as ContractNamespace;
  const key = path.slice(dot + 1);
  if (!CONTRACT_NAMESPACES.includes(ns)) return undefined;
  const obj = context[ns] as Record<string, unknown> | undefined;
  return obj?.[key];
}

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

/** Vorbelegung ohne eigene Angabe in der Vorlage (CO-009): die Muster nennen Korrekturschleifen, aber keine Quelle */
const IMPLIED_PREFILL: Readonly<Record<string, string>> = { free_revisions: "deal.free_revisions", revision_fee: "deal.revision_fee" };

/**
 * Eigene Angaben aus den Stammdaten vorbelegen (CustomVariableDef.prefill): nur leere Angaben und nur, wenn der
 * Systemwert vorhanden ist. Liefert die ergänzten Werte und die Schlüssel, die vorbelegt wurden.
 */
export function prefillValues(defs: readonly CustomVariableDef[], values: Record<string, unknown>, context: ContractContext): { values: Record<string, unknown>; filled: string[] } {
  const out: Record<string, unknown> = { ...values };
  const filled: string[] = [];
  for (const d of defs) {
    const quelle = d.prefill ?? IMPLIED_PREFILL[d.key];
    if (!quelle || !isBlank(out[d.key])) continue;
    const v = contextValue(context, quelle);
    if (isBlank(v)) continue;
    out[d.key] = v;
    filled.push(d.key);
  }
  return { values: out, filled };
}

/** Vergütungsmodelle einer Kooperation (deal_model) mit Bezeichnung für den Vertragstext. */
export const DEAL_MODEL_LABEL: Readonly<Record<string, { de: string; en: string }>> = {
  fixed: { de: "Festpreis", en: "Fixed fee" },
  monthly: { de: "Monatliche Vergütung", en: "Monthly fee" },
  installments: { de: "Vergütung in Raten", en: "Fee in instalments" },
  commission_only: { de: "Nur Provision", en: "Commission only" },
  fixed_plus_commission: { de: "Festpreis zuzüglich Provision", en: "Fixed fee plus commission" },
  custom: { de: "Individuelle Vereinbarung", en: "Individual agreement" },
  barter: { de: "Produkte statt Geld (Barter)", en: "Products instead of money (barter)" },
  event: { de: "Veranstaltung", en: "Event" },
  gifting: { de: "Gifting ohne Veröffentlichungspflicht", en: "Gifting without a duty to publish" },
};
